import React from "react";
import { useTranslation } from "react-i18next";
import { FaCheckDouble } from "react-icons/fa";

import useScrollToTop from "../../../hooks/useScollToTop";
import { useNotification } from "../../../components/Notification/NotificationManager";
import { useUser } from "../../Home/UserContext";
import { useMessage } from "../../../hooks/MessageContext";

import { useMessagerieJudoka } from "./hooks/useMessagerieJudoka";

import MessageHeader from "./components/MessageHeader";
import MessageStats from "./components/MessageStats";
import MessageTabs from "./components/MessageTabs";
import MessageCard from "./components/MessageCard";
import LoadingMessages from "./components/LoadingMessages";
import EmptyMessages from "./components/EmptyMessages";
import ComposeMessageModal from "./components/ComposeMessageModal";

function MessagerieJudoka() {
  const { t } = useTranslation();
  useScrollToTop();

  const { addNotification } = useNotification();
  const { user } = useUser();
  const { setUnreadMessageCount } = useMessage();

  const {
    receivedMessages,
    sentMessages,
    currentMessages,
    activeTab,
    unreadCount,
    loading,
    sending,

    composeModalOpen,
    composeMode,
    messageSubject,
    messageContent,

    setActiveTab,
    setMessageSubject,
    setMessageContent,

    openComposeModal,
    openReplyModal,
    closeComposeModal,

    handleSendMessage,
    handleToggleRead,
    handleMarkAllAsRead,
    handleDeleteMessage,
  } = useMessagerieJudoka({
    user,
    addNotification,
    setUnreadMessageCount,
  });

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-50 px-4 pb-16 pt-28 font-satoshi dark:bg-slate-950 sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(249,115,22,0.14),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(37,99,235,0.16),transparent_32%)]" />
      <div className="absolute inset-x-0 top-0 h-80 bg-slate-950" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-400/80 to-transparent" />

      <section className="relative mx-auto max-w-7xl">
        <MessageHeader
          onCompose={() => openComposeModal("admin")}
          t={t}
        />

        <MessageStats
          receivedCount={receivedMessages.length}
          unreadCount={unreadCount}
          sentCount={sentMessages.length}
        />

        <div className="rounded-[2rem] border border-slate-200 bg-white p-4 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20 sm:p-6">
          <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700 dark:text-sky-200">
                Boîte de messages
              </p>

              <h2 className="mt-2 font-titre text-2xl font-bold text-slate-950 dark:text-white">
                {activeTab === "received"
                  ? t("messagerie.receivedMessages", {
                      defaultValue: "Messages reçus",
                    })
                  : "Messages envoyés"}
              </h2>
            </div>

            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllAsRead}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-white hover:shadow-md dark:border-white/10 dark:bg-white/[0.07] dark:text-white dark:hover:bg-white/10"
              >
                <FaCheckDouble className="text-xs" />
                Tout marquer comme lu
              </button>
            )}
          </div>

          <MessageTabs
            activeTab={activeTab}
            onChange={setActiveTab}
            receivedCount={receivedMessages.length}
            sentCount={sentMessages.length}
            unreadCount={unreadCount}
          />

          {loading && <LoadingMessages />}

          {!loading && currentMessages.length === 0 && (
            <EmptyMessages
              activeTab={activeTab}
              onCompose={() => openComposeModal("admin")}
            />
          )}

          {!loading && currentMessages.length > 0 && (
            <div className="grid gap-5 lg:grid-cols-3">
              {currentMessages.map((message) => (
                <MessageCard
                  key={message.id}
                  message={message}
                  activeTab={activeTab}
                  onToggleRead={handleToggleRead}
                  onDelete={handleDeleteMessage}
                  onReply={openReplyModal}
                  t={t}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {composeModalOpen && (
        <ComposeMessageModal
          mode={composeMode}
          subject={messageSubject}
          content={messageContent}
          onSubjectChange={setMessageSubject}
          onContentChange={setMessageContent}
          onSend={handleSendMessage}
          onClose={closeComposeModal}
          sending={sending}
          t={t}
        />
      )}
    </main>
  );
}

export default MessagerieJudoka;